import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import {
  ArrowRight,
  Brain,
  Globe,
  MessageSquare,
  Network,
  Radio,
  Shield,
  Users,
} from 'lucide-react'
import hivemqLogo from '@/assets/hivemq-bee-neg.svg'
import ioteclipse from '@/assets/ioteclipse.png'
import mqttlogo from '@/assets/mqtt.png'
import sparkplug from '@/assets/sparkplugb.png'
import NumberTicker from '@/components/magicui/number-ticker'
import { PageHeader, Reveal, SectionHeading } from '@/components/shell'

// Richer hover for the cards on this page: lift, brand-tinted border, and a
// spring on the card's icon (targeted by the `card-ic` class).
const sexyHover = {
  transform: 'translateY(-6px)',
  shadow: 'xl',
  borderColor: 'brand.solid',
  '& .card-ic': { transform: 'scale(1.12) rotate(-6deg)' },
} as const

export const Route = createFileRoute('/_app/hivemqintro')({
  component: HiveMQIntroPage,
})

type Stat = { icon: LucideIcon; value: number | null; suffix: string; label: string }
type Feature = { icon: LucideIcon; title: string; description: string }
type Standard = { logo: string; alt: string; name: string; role: string }

const stats: Stat[] = [
  { icon: Users, value: 6000, suffix: '+', label: 'Customers' },
  { icon: Globe, value: 175, suffix: '', label: 'Countries' },
  { icon: MessageSquare, value: 100, suffix: 'M', label: 'Messages/sec' },
  { icon: Shield, value: null, suffix: '99.99%', label: 'Uptime SLA' },
]

const features: Feature[] = [
  {
    icon: Radio,
    title: 'Stream at enterprise scale',
    description:
      'Build a resilient real-time data backbone for always-on, mission-critical operations.',
  },
  {
    icon: Network,
    title: 'Unify OT and IT teams',
    description:
      'Align teams around a single source of governed, contextualized data that powers distributed intelligence.',
  },
  {
    icon: Brain,
    title: 'Feed AI trusted data',
    description: 'Deliver governed, contextualized data that AI can act on with confidence.',
  },
]

const standards: Standard[] = [
  { logo: mqttlogo, alt: 'MQTT', name: 'MQTT Committee', role: 'Technical specification' },
  { logo: sparkplug, alt: 'Sparkplug', name: 'Sparkplug Steering', role: 'Committee member' },
  { logo: ioteclipse, alt: 'Eclipse IoT', name: 'Eclipse IoT', role: 'Working group' },
]

function HiveMQIntroPage() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        centered
        title="The Real-Time Industrial Data Platform for Agentic AI"
        subtitle="Turn industrial data into action - a trusted path from MQTT streaming to real-time data intelligence and governed action."
      />

      <VStack align="stretch" gap={12}>
        {/* Hero identity */}
        <Reveal>
          <VStack gap={5} textAlign="center">
            <Image src={hivemqLogo} alt="HiveMQ" h="90px" objectFit="contain" />
            <HStack gap={2} flexWrap="wrap" justify="center">
              {['Data Streaming', 'Data Intelligence', 'Agentic AI'].map((pill) => (
                <Box
                  key={pill}
                  px={4}
                  py={1.5}
                  bg="brand.solid"
                  borderRadius="full"
                  transition="transform 0.2s ease, box-shadow 0.2s ease"
                  _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
                >
                  <Text fontSize="sm" fontWeight="semibold" color="gray.900" letterSpacing="0.01em">
                    {pill}
                  </Text>
                </Box>
              ))}
            </HStack>
          </VStack>
        </Reveal>

        {/* Stats row */}
        <Reveal>
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
            {stats.map((stat) => (
              <Card.Root
                key={stat.label}
                borderWidth="1px"
                borderColor="border"
                transition="transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease"
                _hover={sexyHover}
              >
                <Card.Body p={6}>
                  <VStack gap={3}>
                    <Flex
                      className="card-ic"
                      boxSize={12}
                      bg="brand.solid"
                      color="gray.900"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      transition="transform 0.25s ease"
                    >
                      <stat.icon size={22} />
                    </Flex>
                    <VStack gap={1}>
                      <Text fontSize="3xl" fontWeight="bold" lineHeight="1">
                        {stat.value !== null ? <NumberTicker value={stat.value} /> : null}
                        {stat.suffix}
                      </Text>
                      <Text
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="0.08em"
                        color="content.secondary"
                      >
                        {stat.label}
                      </Text>
                    </VStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            ))}
          </SimpleGrid>
        </Reveal>

        {/* About */}
        <Reveal>
          <VStack align="stretch" gap={6}>
            <Box textAlign="center">
              <SectionHeading title="What is HiveMQ?" />
            </Box>
            <Text
              maxW="3xl"
              mx="auto"
              textAlign="center"
              color="content.secondary"
              lineHeight="1.7"
            >
              HiveMQ is the real-time industrial data platform powering Agentic AI, built on MQTT.
              It creates a trusted path from MQTT streaming to real-time data intelligence and
              governed action - connecting, contextualizing, analyzing, and acting on operational
              data to deliver trusted, AI-ready pipelines with measurable outcomes. Designed for
              always-on, mission-critical environments, HiveMQ streams data from edge to cloud at
              enterprise scale and unifies OT and IT teams around governed, contextualized data.
              Leading enterprises including BMW, Mercedes-Benz, Ford, Eli Lilly, Unilever, and Dell
              trust HiveMQ to improve reliability, efficiency, and decision-making as they move from
              connected assets to intelligent, autonomous operations.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              {features.map((feature) => (
                <Card.Root
                  key={feature.title}
                  borderWidth="1px"
                  borderColor="border"
                  transition="transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease"
                  _hover={sexyHover}
                >
                  <Card.Body p={6}>
                    <VStack align="start" gap={3}>
                      <Flex
                        className="card-ic"
                        boxSize={10}
                        bg="bg.subtle"
                        color="content.primary"
                        borderRadius="lg"
                        align="center"
                        justify="center"
                        transition="transform 0.25s ease"
                      >
                        <feature.icon size={20} />
                      </Flex>
                      <Text fontWeight="semibold">{feature.title}</Text>
                      <Text fontSize="sm" color="content.secondary" lineHeight="1.6">
                        {feature.description}
                      </Text>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          </VStack>
        </Reveal>

        {/* Standards */}
        <Reveal>
          <VStack align="stretch" gap={6}>
            <Box textAlign="center">
              <SectionHeading
                title="Shaping the future of industrial connectivity"
                description="Active contributors to the standards that define mission-critical data communication"
              />
            </Box>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
              {standards.map((standard) => (
                <Card.Root
                  key={standard.name}
                  borderWidth="1px"
                  borderColor="border"
                  transition="transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease"
                  _hover={{
                    transform: 'translateY(-6px)',
                    shadow: 'xl',
                    borderColor: 'brand.solid',
                  }}
                >
                  <Card.Body p={6}>
                    <VStack gap={4}>
                      <Flex
                        h="90px"
                        w="100%"
                        bg="bg.subtle"
                        borderRadius="xl"
                        align="center"
                        justify="center"
                        px={6}
                      >
                        <Image
                          src={standard.logo}
                          alt={standard.alt}
                          h="45px"
                          objectFit="contain"
                        />
                      </Flex>
                      <VStack gap={0}>
                        <Text fontWeight="semibold">{standard.name}</Text>
                        <Text fontSize="sm" color="content.secondary">
                          {standard.role}
                        </Text>
                      </VStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          </VStack>
        </Reveal>

        {/* CTA */}
        <Reveal>
          <Card.Root
            bg="bg.subtle"
            borderWidth="1px"
            borderColor="border"
            borderLeftWidth="4px"
            borderLeftColor="brand.solid"
            transition="box-shadow 0.25s ease"
            _hover={{ shadow: 'lg' }}
          >
            <Card.Body p={8}>
              <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
                <VStack align="start" gap={1}>
                  <Heading size="lg">Ready to dive deeper?</Heading>
                  <Text color="content.secondary">
                    Explore the platform journey from streaming to intelligence to agentic
                    operations
                  </Text>
                </VStack>
                <Button size="lg" onClick={() => navigate({ to: '/hivemqvision' })}>
                  Explore Our Vision
                  <ArrowRight size={18} />
                </Button>
              </HStack>
            </Card.Body>
          </Card.Root>
        </Reveal>
      </VStack>
    </Box>
  )
}
